import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

import CategoryNav from '../components/CategoryNav/CategoryNav';
import ArticleGrouping from '../components/ArticleGrouping/ArticleGrouping';
import StoryCarousel from '../components/StoryCarousel/StoryCarousel';
import DateSelector from '../components/DateSelector/DateSelector';
import LowImpactPanel from '../components/LowImpactPanel/LowImpactPanel';

import {
  fetchStories,
  fetchTopStories,
  topStoriesQueryKey,
  fetchLowImpactStories,
  lowImpactStoriesQueryKey,
} from '../api/storiesApi';
import { useIntersection } from '../hooks/useIntersection';
import { todayIso } from '../hooks/useDateState';
import type { StoryItem } from '../types/api';

import styles from './NewspaperPage.module.css';

export default function NewspaperPage() {
  const { lang = 'en', city = '' } = useParams<{ lang: string; city: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  // Debounce sentinel intersection to prevent multiple rapid fetches on page restore
  const lastFetchTimeRef = useRef<number>(0);
  const FETCH_DEBOUNCE_MS = 500;

  // Keep radiri-last-selection in sync with the current URL so the account
  // settings page (and CityPickerPage auto-redirect) always reflect reality.
  useEffect(() => {
    if (!lang || !city) return;
    try {
      localStorage.setItem(
        'radiri-last-selection',
        JSON.stringify({ lang, citySlug: city }),
      );
    } catch { /* non-fatal */ }
  }, [lang, city]);

  const today = todayIso();
  const activeCategory = searchParams.get('category') ?? 'all';
  const date = searchParams.get('date') ?? today;

  function setDate(d: string) {
    const p: Record<string, string> = {}
    if (activeCategory !== 'all') p.category = activeCategory
    if (d !== today) p.date = d
    setSearchParams(p)
  }

  const setActiveCategory = (cat: string) => {
    const p: Record<string, string> = {}
    if (cat !== 'all') p.category = cat
    if (date !== today) p.date = date
    setSearchParams(p)
  };

  const { data: topData } = useQuery({
    queryKey: topStoriesQueryKey(city, lang, activeCategory, date !== today ? date : undefined),
    queryFn: () => fetchTopStories(
      city,
      lang,
      activeCategory === 'all' ? undefined : activeCategory,
      7,
      10,
      date !== today ? date : undefined,
    ),
    enabled: Boolean(city),
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ['stories', city, lang, activeCategory, date],
    queryFn: ({ pageParam }) =>
      fetchStories(
        city,
        lang,
        activeCategory === 'all' ? undefined : activeCategory,
        pageParam as number,
        10,
        7,
        date !== today ? date : undefined,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam: number) =>
      lastPage.has_next ? (lastPageParam as number) + 1 : undefined,
    enabled: Boolean(city),
  });

  const { ref: sentinelRef, isIntersecting } = useIntersection({ rootMargin: '200px' });

  useEffect(() => {
    // Only trigger next-page fetch once page 1 has loaded (sentinel is in the viewport
    // immediately on a fresh category switch before any content fills the page).
    const hasContent = (infiniteData?.pages.length ?? 0) > 0;
    if (isIntersecting && hasNextPage && !isFetchingNextPage && hasContent) {
      // Debounce rapid intersection changes (common on page restore with scroll restoration)
      const now = performance.now();
      if (now - lastFetchTimeRef.current >= FETCH_DEBOUNCE_MS) {
        lastFetchTimeRef.current = now;
        void fetchNextPage();
      }
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage, infiniteData?.pages.length]);

  const { data: lowImpactData } = useQuery({
    queryKey: lowImpactStoriesQueryKey(city, lang, activeCategory, date !== today ? date : undefined),
    queryFn: () => fetchLowImpactStories(
      city,
      lang,
      activeCategory === 'all' ? undefined : activeCategory,
      date !== today ? date : undefined,
    ),
    enabled: Boolean(city),
  });

  const lowImpactStories: StoryItem[] = lowImpactData ?? [];

  const topStories: StoryItem[] = topData ?? [];
  // Featured leader is the highest-ranked top story that has related stories.
  // If none have related stories, no featured group is shown (all stories appear as singletons).
  const leader = topStories.find((s) => (s.related_stories?.length ?? 0) > 0) ?? null;
  const secondary = leader?.related_stories ?? [];

  // On small screens the inline panel appears after the first 5 story units.
  // The featured leader (if present) counts as 1 unit, so we show 4 or 5 items
  // from the infinite list before it. Once the first page loads the panel is
  // permanently anchored — subsequent pages only append below it.
  // Wide screens use the sidebar; the inline element is CSS-hidden there.
  const LOW_IMPACT_INSERT_AFTER = leader ? 4 : 5;

  const allInfiniteStories: StoryItem[] = infiniteData?.pages.flatMap((p) => p.stories) ?? [];

  // Collect all cluster_ids that appear as secondaries in any story's related_stories.
  // These should not appear again as standalone story cards.
  const secondaryClusterIds = new Set<number>(
    [...topStories, ...allInfiniteStories].flatMap((s) =>
      (s.related_stories ?? []).map((r) => r.cluster_id)
    )
  );

  // Remove the featured leader and all secondaries from the infinite scroll list.
  // Also remove any low-impact stories that somehow appear (belt-and-suspenders).
  const lowImpactClusterIds = new Set<number>(lowImpactStories.map((s) => s.cluster_id));
  const infiniteStories = allInfiniteStories.filter(
    (s) =>
      s.cluster_id !== leader?.cluster_id &&
      !secondaryClusterIds.has(s.cluster_id) &&
      !lowImpactClusterIds.has(s.cluster_id)
  );

  const storiesBeforePanel = infiniteStories.slice(0, LOW_IMPACT_INSERT_AFTER);
  const storiesAfterPanel = infiniteStories.slice(LOW_IMPACT_INSERT_AFTER);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <DateSelector date={date} onChange={setDate} maxDate={today} />
      </div>

      <div className={styles.categorySection}>
        <CategoryNav
          citySlug={city}
          lang={lang}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          date={date !== today ? date : undefined}
        />
      </div>

      <StoryCarousel criterion="top" citySlug={city} lang={lang} category={activeCategory} date={date !== today ? date : undefined} />

      <div className={styles.contentArea}>
        <div className={styles.mainContent}>
          {leader && (
            <div className={styles.topSection}>
              <ArticleGrouping leader={leader} secondary={secondary} />
            </div>
          )}

          <div className={styles.storyList}>
            {storiesBeforePanel.map((story) => (
              <ArticleGrouping key={story.cluster_id} leader={story} secondary={story.related_stories ?? []} />
            ))}
            {lowImpactStories.length > 0 && (
              <div className={styles.lowImpactInline}>
                <LowImpactPanel stories={lowImpactStories} />
              </div>
            )}
            {storiesAfterPanel.map((story) => (
              <ArticleGrouping key={story.cluster_id} leader={story} secondary={story.related_stories ?? []} />
            ))}
          </div>
        </div>

        {lowImpactStories.length > 0 && (
          <aside className={styles.lowImpactSidebar} aria-label={t('low_impact_stories')}>
            <LowImpactPanel stories={lowImpactStories} />
          </aside>
        )}
      </div>

      {isError && <p className={styles.errorMsg}>{t('error_loading')}</p>}

      {!leader && infiniteStories.length === 0 && !isError && !isFetchingNextPage && (
        <p className={styles.emptyMsg}>{t('no_stories')}</p>
      )}

      <div ref={sentinelRef} className={styles.sentinel} />
      {isFetchingNextPage && <p className={styles.loadingMore}>{t('loading')}</p>}
    </div>
  );
}

